import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  NativeSelect,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  addBulkMatchedWholegoods,
  addDmsWhoolegoods,
  removeAllDmsWhoolegoods,
  useGetMatchingProductsV2Mutation,
  useGetNotSyncedDmsWholegoodsMutation,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import {
  LocationWholegood,
  MatchedProduct,
} from "../../../models/features/Integration/DmsSetup";
import { DmsWhlegoodRequestDto } from "../../../models/features/ShoppingPortal/DmsPart";
import { AppFilter } from "../../../models/app/AppModels";
import ServerAppGrid from "../../app/ServerAppGrid";
import {
  GridColDef,
  GridValueGetterParams,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { AppGridId } from "../../../models/app/AppGridModels";
import { GetAppGridColumns } from "../../../services/utilities/utility";
import { DmsManufacturer } from "../../../models/features/ShoppingPortal/DmsManufacturer";
import TruncatedCellWithTooltip from "../../common/TruncatedCellWithTooltip";

interface NonSyncedDmsProductsProps {
  handleNext: any;
  handleStep: (step: number) => () => void;
}

const NonSyncedDmsProducts: React.FunctionComponent<
  NonSyncedDmsProductsProps
> = ({ handleNext, handleStep }) => {
  const dispatch = useDispatch();
  const [withQtyOnly, setWithQtyOnly] = React.useState(true);
  const [selectedManufacturers, setSelectedManufacturers] = React.useState<
    DmsManufacturer[]
  >([]);
  const [dmsWholegoodsManufacturers, setDMSWholegoodsManufacturers] =
    React.useState<DmsManufacturer[]>([]);
  const WGgridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.ReInitiate_Setup_DMS_Products
    )
  );
  const selectedProducts = useSelector(
    (state: AppState) => state.dmsReducer.dmsWholeGoods
  );
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  const { dmsWholeGoods, dmsParts, dmsManufacturers } = useSelector(
    (state: AppState) => state.dmsReducer
  );

  const [paging, setPaging] = React.useState<AppFilter>({
    pageIndex: 0,
    pageSize: 100,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
  });

  const [productsCount, setProductsCount] = React.useState<number>(0);
  const [wholegoods, setWholegoods] = React.useState<LocationWholegood[]>([]);

  const [dmsWhoolgoodsMutation, dmsWhoolgoodsMutationResult] =
    useGetNotSyncedDmsWholegoodsMutation();
  const [getMatchingWhoolegoods, matchingWhoolegoodsMutationResult] =
    useGetMatchingProductsV2Mutation();

  const fetchDMSWholegoodsData = () => {
    let dmsManufacturersList: string[] = [];

    if (selectedManufacturers.length > 0) {
      dmsManufacturersList = selectedManufacturers.map((n) => n.manufacturerId);
    } else {
      dmsManufacturersList = dmsWholegoodsManufacturers.map(
        (n) => n.manufacturerId
      );
    }

    let request: DmsWhlegoodRequestDto = {
      dealerIds:
        dmsSetup?.dmsLocations
          .filter((n) => n.shopifyLocationId > 0)
          .map((n) => n.dealerId.toString()) ?? [],
      search: paging.quickFilterValues.join(" "),
      pageNumber: paging.pageIndex,
      pageSize: paging.pageSize,
      searchModels: [],
      stockingOnly: false,
      wildCardSearch: true,
      excludePricing: true,
      withQtyOnly: withQtyOnly,
      excludeInventoryModelId: [],
      searchManufacturerIds: dmsManufacturersList,
    };
    dmsWhoolgoodsMutation({ data: request });
  };

  React.useEffect(() => {
    fetchDMSWholegoodsData();
  }, [dmsWholegoodsManufacturers, paging, selectedManufacturers]);

  React.useEffect(() => {
    if (
      dmsWhoolgoodsMutationResult.data &&
      dmsWhoolgoodsMutationResult.isSuccess
    ) {
      let totalRecords = dmsWhoolgoodsMutationResult.data.totalRecords;
      setWholegoods(dmsWhoolgoodsMutationResult.data.entityList);
      setProductsCount(totalRecords);
    }
  }, [dmsWhoolgoodsMutationResult]);

  React.useEffect(() => {
    if (dmsManufacturers && dmsManufacturers.length > 0) {
      let mfrs =
        dmsManufacturers
          .filter((x) => x.isWholegoodManufacturer)
          .sort((a, b) => a.name.localeCompare(b.name)) ?? [];
      setDMSWholegoodsManufacturers(mfrs);
      setSelectedManufacturers(mfrs);
    }
  }, [dmsManufacturers]);

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWithQtyOnly(e.target.checked);
    setPaging((prev) => ({ ...prev, pageIndex: 0 }));
    dispatch(removeAllDmsWhoolegoods());
  };

  const onManufacturerChange = (value: DmsManufacturer[]) => {
    setSelectedManufacturers(value);
    dispatch(removeAllDmsWhoolegoods());
  };

  const handleItemSelect = (items: any[]) => {
    if (items.length === 0) {
      dispatch(addBulkMatchedWholegoods([]));
    }
    const updatedSelectedProducts =
      selectedProducts.filter((sp) => items.includes(sp.inventoryModelId)) ??
      [];

    wholegoods.forEach((item) => {
      if (
        items.includes(item.inventoryModelId) &&
        !selectedProducts.some(
          (sp) => sp.inventoryModelId === item.inventoryModelId
        )
      ) {
        updatedSelectedProducts.push(item);
      }
    });
    dispatch(
      addDmsWhoolegoods({
        whoolegoods: updatedSelectedProducts,
      })
    );
  };

  const handleNextStep = () => {
    getMatchingWhoolegoods({
      wholegoods: dmsWholeGoods ?? [],
      parts: dmsParts ?? [],
      dmsSetupId: dmsSetup?.id,
      dmsManufacturers: dmsWholegoodsManufacturers,
    }).then((res: any) => {
      if (res.data && res.data.entityList) {
        var matchAndMissMatchArray: MatchedProduct[] = [];
        res.data.entityList.forEach((n: any) => {
          matchAndMissMatchArray.push(n);
        });
        dispatch(addBulkMatchedWholegoods([...matchAndMissMatchArray]));
        handleNext();
      }
    });
  };

  function CustomFilterInputValue(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;

    const ratingRef: React.Ref<any> = React.useRef(null);

    const handleDropdownFilterChange = (event: any) => {
      applyValue({
        ...item,
        value: event.target.value !== "all" ? event.target.value : undefined,
      });
    };
    return (
      <FormControl fullWidth>
        <InputLabel variant="standard" htmlFor="uncontrolled-native">
          Manufacturer
        </InputLabel>
        <NativeSelect
          ref={ratingRef}
          value={item.value ? item.value : "all"}
          defaultValue="all"
          onChange={handleDropdownFilterChange}
        >
          <option value={"all"}>All</option>
          {dmsWholegoodsManufacturers.map((n) => (
            <option value={n.manufacturerId}>{n.name}</option>
          ))}
        </NativeSelect>
      </FormControl>
    );
  }

  const customFilterOperators: GridFilterOperator[] = [
    {
      label: "Equals",
      value: "equals",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.columnField ||
          !filterItem.value ||
          !filterItem.operatorValue
        ) {
          return null;
        }

        return (params): boolean => {
          return params.value == filterItem.value;
        };
      },
      InputComponent: CustomFilterInputValue,
      InputComponentProps: { type: "number" },
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "manufacturer",
      headerName: "Manufacturer",
      flex: 1,
      filterOperators: customFilterOperators,
      valueGetter: (params: GridValueGetterParams) => params.row.mfgName,
      renderCell: (params: GridRenderCellParams) => {
        return <TruncatedCellWithTooltip value={params.row.mfgName} />;
      },
    },
    {
      field: "model",
      headerName: "Model",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.model} />;
      },
    },
    {
      field: "productLine",
      headerName: "Category",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.productLine} />;
      },
    },
    {
      field: "subProduct",
      headerName: "Sub-Category",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.subProduct} />;
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params.row.description}>
            <Typography
              className="truncate-ellipsis"
              sx={{ fontSize: "0.875rem" }}
            >
              {params.row.description}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "cost",
      headerName: "Cost ($)",
      headerAlign: "right",
      flex: 1,
      align: "right",
      type: "number",
      filterable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.cost.toFixed(2),
    },
    {
      field: "price",
      headerName: "MSRP ($)",
      headerAlign: "right",
      flex: 1,
      type: "number",
      filterable: false,
      align: "right",
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.price.toFixed(2),
    },
    {
      field: "count",
      headerName: "Stock",
      align: "right",
      headerAlign: "right",
      type: "number",
      flex: 1,
      filterable: false,
      valueGetter: (params: GridValueGetterParams<LocationWholegood>) => {
        if (params.row?.locationCount) {
          return params.row?.locationCount?.reduce(
            (partial: number, value: any) => partial + value.count,
            0
          );
        } else {
          return 0;
        }
      },
    },
  ];

  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={2}
        p={0.5}
      >
        <Alert severity="info">
          Please select wholegoods you want to sync to the website. We will
          automatically try to match these with images & descriptions from our
          product catalog.
        </Alert>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            size="small"
            variant="contained"
            endIcon={
              matchingWhoolegoodsMutationResult.isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <></>
              )
            }
            disabled={selectedProducts.length === 0}
            onClick={handleNextStep}
          >
            Next
          </Button>
        </Box>
      </Box>
      <ServerAppGrid
        gridId={AppGridId.ReInitiate_Setup_DMS_Products}
        getRowId={(row) => row.inventoryModelId}
        data={wholegoods.map((item: any, index: any) => ({
          ...item,
          id: index,
          subProduct: item.subProduct ?? "",
          productLine: item.productLine ?? "",
          description: item.description ?? "",
        }))}
        columns={GetAppGridColumns(
          columns,
          WGgridSetting?.columnVisibilityModel
        )}
        isLoading={dmsWhoolgoodsMutationResult.isLoading}
        paging={paging}
        setPaging={setPaging}
        totalRecords={productsCount}
        selectedRows={selectedProducts.map((n) => n.inventoryModelId)}
        onSelect={(newSelection: any) => {
          handleItemSelect(newSelection);
        }}
        hideColumnFilter={false}
        height="60vh"
        hidePageSize={true}
        customToolbar={true}
        switchLabel="In-stock"
        switchChecked={withQtyOnly}
        onSwitchChange={handleSwitchChange}
        manufacturerOptions={dmsWholegoodsManufacturers}
        selectedManufacturers={selectedManufacturers}
        onManufacturerChange={onManufacturerChange}
      />
    </>
  );
};

export default NonSyncedDmsProducts;
